<script lang="ts">
  /**
   * SetHrLineChart — per-set ECharts red-line HR chart for SetDetailSheet.
   *
   * Receives pre-computed SetHrLineChartData from buildSetHrLineChart() and
   * mounts an ECharts canvas chart. Mirrors P2HrTimeline's mount pattern:
   *   - echarts.init on bind:this el in onMount
   *   - mountChart() handles resize + dispose cleanup
   *   - COLORS.coralRed for the line + fill gradient
   *   - avg BPM shown as ONE stroke-colored dash per length (custom series)
   *   - HR line breaks at lengths with no samples (connectNulls: false)
   *   - DENSE (>=70% coverage): clean line + coral gradient fill, no dots
   *   - SPARSE (<70% coverage): small coral dots at known readings, no fill
   *   - peak markPoint
   *   - x-axis = length number (1..lapCount), y-axis = BPM with min/max labels
   *   - height controlled by CSS (120px), width fills container
   *
   * No-HR fallback: when data is null, renders nothing (caller already gates on hrStats).
   *
   * Accessibility: aria-hidden="true" on the container — the ".hr-stat" line
   * below it in SetDetailSheet carries the screen-reader content (avg + peak BPM).
   */
  import { onMount } from "svelte";
  import echarts from "../lib/echarts";
  import { mountChart } from "../lib/echartsHelpers";
  import { COLORS } from "../lib/designTokens";
  import type { SetHrLineChartData } from "../lib/setHrLineChart";

  interface Props {
    data: SetHrLineChartData;
  }
  let { data }: Props = $props();

  let el: HTMLDivElement;
  let chart: ReturnType<typeof echarts.init> | undefined;

  /** (Re)apply the ECharts option from the CURRENT `data`. Called on mount AND
   *  from the $effect below on every data change, so editing a stroke (which
   *  recolors an avg-dash segment) redraws the chart instead of leaving the bar
   *  frozen while the plain-markup legend updates (Scott UAT 2026-06-17). */
  function applyOption() {
    if (!chart) return;

    // Avg-dash series: one short horizontal dash per length, drawn at the
    // y-level of the set's global avgBpm, colored by that length's stroke.
    // Replaces the old solid top ribbon + old uniform dashed markLine.
    //
    // Each segment spans [xStart, xEnd) = [i+0.5, i+1.5) with a small inset
    // (GAP_PX on each side) so consecutive dashes read as distinct marks.
    //
    // renderItem receives the pre-projected canvas coords from api.coord().
    const DASH_H = 3; // height in canvas pixels
    const GAP_PX = 2; // inset from each edge so dashes don't touch

    // Band opacity per stroke family (mirrors hrBandChart.ts bandOpacity()).
    // Raise Backstroke floor to 0.35 here only — keep data model pure.
    function bandOpacity(color: string): number {
      switch (color) {
        case "#FF5722": return 0.44;               // Butterfly
        case "#FBC02D": return Math.max(0.35, 0.32); // Backstroke: floor to 0.35
        case "#19C6BE": return 0.38;               // Breaststroke
        default:        return 0.42;               // Freestyle, Unknown, others
      }
    }

    // Band series: one full-height rect per length, filled with stroke color.
    // Rendered BELOW (series index 0) the HR line and avg-dash (z:10).
    // Each rect: x spans [i-0.5, i+0.5] (same geometry as avgDashSeries x).
    // Height spans the full chart y range — ECharts clips to the grid automatically.
    const yMin = Math.floor((data.minBpm * 0.92) / 10) * 10;
    const yMax = Math.ceil((data.peakBpm * 1.10) / 10) * 10;

    const bandSeries = {
      type: "custom" as const,
      // data tuple: [xStart, xEnd, color]
      data: data.lengths.map((entry) => [
        entry.lengthIndex - 0.5,
        entry.lengthIndex + 0.5,
        entry.color,
      ]),
      renderItem: (
        _params: unknown,
        api: {
          coord: (pt: [number, number]) => [number, number];
          value: (idx: number) => number | string;
        },
      ) => {
        const xLeft  = api.coord([api.value(0) as number, yMin])[0];
        const xRight = api.coord([api.value(1) as number, yMin])[0];
        const yTop   = api.coord([0, yMax])[1];
        const yBot   = api.coord([0, yMin])[1];
        const color  = api.value(2) as string;
        const w = Math.max(xRight - xLeft, 1);
        const h = Math.max(yBot - yTop, 1);
        return {
          type: "rect",
          shape: { x: xLeft, y: yTop, width: w, height: h },
          style: { fill: color, opacity: bandOpacity(color) },
        };
      },
      z: 1, // below HR line (default z) and avgDashSeries (z:10)
      animation: false,
    };

    // Neutral light-grey color for the avg reference line — NOT a stroke color.
    // Keeps it distinct from every stroke band (Breaststroke teal, Butterfly orange, etc.)
    // so it reads as "reference" over any background. FIX 1.
    const AVG_DASH_COLOR = "rgba(255,255,255,0.55)";

    const avgDashSeries = {
      type: "custom" as const,
      // data tuple: [xStart, xEnd] — color fixed to neutral, not stroke color
      data: data.lengths.map((entry) => [
        entry.lengthIndex - 0.5, // xStart (left edge of this length's cell)
        entry.lengthIndex + 0.5, // xEnd   (right edge)
      ]),
      renderItem: (
        _params: unknown,
        api: {
          coord: (pt: [number, number]) => [number, number];
          value: (idx: number) => number | string;
        },
      ) => {
        const xLeft = api.coord([api.value(0) as number, data.avgBpm])[0];
        const xRight = api.coord([api.value(1) as number, data.avgBpm])[0];
        const yCenter = api.coord([0, data.avgBpm])[1];
        const segW = Math.max(xRight - xLeft - GAP_PX * 2, 1);
        return {
          type: "rect",
          shape: {
            x: xLeft + GAP_PX,
            y: yCenter - DASH_H / 2,
            width: segW,
            height: DASH_H,
          },
          style: { fill: AVG_DASH_COLOR, opacity: 1 },
        };
      },
      z: 10,
      animation: false,
    };

    // HR line: one entry per length — bpm value when known, null when no HR samples.
    const hrData = data.lengths.map((entry) =>
      entry.bpm !== null ? [entry.lengthIndex, entry.bpm] : [entry.lengthIndex, null],
    );

    // Adaptive coverage: dense (>=70% lengths have HR) vs sparse (<70%).
    // Dense → clean line + gradient fill, no dots (noise-free read).
    // Sparse → dots only, no fill (don't imply continuity when data is patchy).
    const withHr = data.lengths.filter((l) => l.bpm !== null).length;
    const coverage = withHr / data.lapCount;
    const dense = coverage >= 0.7;

    const hrSeriesConfig = {
      name: "Heart rate",
      type: "line" as const,
      data: hrData,
      smooth: 0.35,
      // connectNulls: true → one continuous solid coral line through all the
      // readings, bridging no-reading lengths. Honesty comes from the sparse
      // dots marking real readings + the "X of N lengths" coverage note.
      connectNulls: true,
      // Dark shadow halo keeps the coral line crisp over every stroke band color,
      // including orange Butterfly where coral-on-orange contrast is weakest. FIX 3.
      lineStyle: { color: COLORS.coralRed, width: 2, shadowColor: "rgba(0,0,0,0.55)", shadowBlur: 4 },
      itemStyle: { color: COLORS.coralRed },
      ...(dense
        ? {
            showSymbol: false,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(255,87,34,0.28)" },
                { offset: 1, color: "rgba(255,87,34,0.00)" },
              ]),
            },
          }
        : {
            showSymbol: true,
            symbolSize: 4,
            symbol: "circle",
          }),
      // Small coral dot at the peak of the HR line
      markPoint: {
        silent: true,
        data: [{ type: "max" }],
        symbol: "circle",
        symbolSize: 7,
        label: { show: false },
        itemStyle: { color: COLORS.coralRed },
      },
      // No in-chart avg label — the number appears only once, in the .hr-stat
      // line below the legend. FIX 2: markLine removed entirely.

    };

    chart.setOption(
      {
        backgroundColor: "transparent",
        grid: {
          left: 36,
          right: 12,
          top: 16,
          bottom: 24,
        },
        xAxis: {
          type: "value",
          min: 0.5,
          max: data.lapCount + 0.5,
          interval: 1,
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
          axisLabel: {
            color: COLORS.textDim,
            fontSize: 10,
            // Show label 1, middle, and last length number only to avoid crowding
            formatter: (v: number) => {
              const mid = Math.round((data.lapCount + 1) / 2);
              if (v === 1 || v === mid || v === data.lapCount) return String(v);
              return "";
            },
          },
          splitLine: { show: false },
        },
        yAxis: {
          type: "value",
          // Pad ~10% above peak and ~8% below min, rounded to nearest 10 for clean labels
          min: Math.floor((data.minBpm * 0.92) / 10) * 10,
          max: Math.ceil((data.peakBpm * 1.10) / 10) * 10,
          axisLine: { show: false },
          axisLabel: {
            color: COLORS.textDim,
            fontSize: 10,
            // Show only min and max labels on y-axis (the two anchor values)
            formatter: (v: number) => {
              const yMin = Math.floor((data.minBpm * 0.92) / 10) * 10;
              const yMax = Math.ceil((data.peakBpm * 1.10) / 10) * 10;
              if (Math.abs(v - yMin) < 5 || Math.abs(v - yMax) < 5) return String(Math.round(v));
              return "";
            },
          },
          splitLine: {
            lineStyle: { color: "rgba(255,255,255,0.05)", type: "solid" },
          },
        },
        tooltip: { show: false }, // no tooltip — mobile touch doesn't benefit here
        series: [bandSeries, hrSeriesConfig, avgDashSeries],
      },
      true,
    );
  }

  onMount(() => mountChart(el, (c) => { chart = c; applyOption(); }));

  // Redraw whenever `data` changes (e.g. a stroke edit recolors an avg dash).
  // Without this the chart stays frozen at its mount-time option while the
  // plain-markup legend updates — the bug Scott caught in UAT.
  $effect(() => {
    void data;
    applyOption();
  });
</script>

<div class="chart-wrap" aria-hidden="true" bind:this={el}></div>

<style>
  .chart-wrap {
    width: 100%;
    height: 120px;
    display: block;
    margin-top: 16px;
    margin-bottom: 10px;
  }
</style>
